import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
    Wallet, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle,
    Filter, ChevronDown, ChevronUp, X, Info,
} from 'lucide-react';

import {
    fetchCCTypes, fetchFinancialYears, fetchCCList,
    fetchConsolidateCashflowReport, fetchCashflowDetail, fetchInvoiceReceiptDetail,
    clearReport, clearInvoiceDetail,
    selectConsolidateCashflowReport,
} from '../../slices/ReportsSlice/consolidateCashflowReportSlice';

const fmt = (v) => {
    const n = parseFloat(v);
    if (!n) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const joinIds = (arr) => (arr && arr.length ? arr.join(',') + ',' : '');

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';
const td = 'px-2 py-1 text-xs';

const CashflowDetailRow = ({ row }) => (
    <div className="overflow-x-auto bg-gray-50 dark:bg-gray-900/40 p-2">
        {row.loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-3 justify-center"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading detail…</div>
        ) : !row.data || row.data.length === 0 ? (
            <div className="text-center text-xs text-gray-400 py-3">No transactions found.</div>
        ) : (
            <table className="w-full text-[11px] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <thead>
                    <tr className="bg-gray-200 dark:bg-gray-800 text-left">
                        {['Invoice Date', 'Paid Date', 'Description', 'IT Code', 'Int. Transfer Credit', 'Int. Transfer Debit', 'Cash Paid', 'Cash Payable', 'Bank Paid', 'Bank Payable', 'Total'].map((h) => (
                            <th key={h} className="px-1.5 py-1 font-bold text-gray-600 dark:text-gray-300 uppercase whitespace-nowrap">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {row.data.map((d, i) => (
                        <tr key={i} className="bg-white dark:bg-gray-800">
                            <td className="px-1.5 py-1 whitespace-nowrap">{d.InvoiceDate}</td>
                            <td className="px-1.5 py-1 whitespace-nowrap">{d.PaidDate}</td>
                            <td className="px-1.5 py-1">{d.Description}</td>
                            <td className="px-1.5 py-1">{d.ITCode}</td>
                            <td className="px-1.5 py-1 text-right">{fmt(d.InternalTransferCredit)}</td>
                            <td className="px-1.5 py-1 text-right">{fmt(d.InternalTransferDebit)}</td>
                            <td className="px-1.5 py-1 text-right">{fmt(d.CashDebitPaid)}</td>
                            <td className="px-1.5 py-1 text-right">{fmt(d.CashDebitPayable)}</td>
                            <td className="px-1.5 py-1 text-right">{fmt(d.InvoiceBankDebitPaid)}</td>
                            <td className="px-1.5 py-1 text-right">{fmt(d.InvoiceBankDebitPayable)}</td>
                            <td className="px-1.5 py-1 text-right font-semibold">{fmt(d.Total)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const OverheadRow = ({ label, cur, prev, total, showPrev, onClickCur, onClickPrev, onClickTotal }) => (
    <div className="grid grid-cols-4 gap-2 items-center px-3 py-1 border-b border-gray-100 dark:border-gray-700 text-xs">
        <div className="text-gray-600 dark:text-gray-300 font-medium">{label}</div>
        <div className="text-right">
            {onClickCur ? (
                <button onClick={onClickCur} disabled={!cur} className="text-emerald-600 dark:text-emerald-400 font-semibold hover:underline disabled:no-underline disabled:opacity-60">{fmt(cur)}</button>
            ) : <span className="font-semibold text-gray-800 dark:text-gray-100">{fmt(cur)}</span>}
        </div>
        {showPrev ? (
            <div className="text-right">
                {onClickPrev ? (
                    <button onClick={onClickPrev} disabled={!prev} className="text-emerald-600 dark:text-emerald-400 font-semibold hover:underline disabled:no-underline disabled:opacity-60">{fmt(prev)}</button>
                ) : <span className="font-semibold text-gray-800 dark:text-gray-100">{fmt(prev)}</span>}
            </div>
        ) : <div />}
        <div className="text-right">
            {onClickTotal ? (
                <button onClick={onClickTotal} disabled={!total} className="text-emerald-600 dark:text-emerald-400 font-bold hover:underline disabled:no-underline disabled:opacity-60">{fmt(total)}</button>
            ) : <span className="font-bold text-gray-900 dark:text-white">{fmt(total)}</span>}
        </div>
    </div>
);

const InvoiceDetailModal = ({ title, rows, loading, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-4">
                {loading ? (
                    <div className="flex items-center justify-center gap-3 py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /><span className="text-sm text-gray-500">Loading…</span></div>
                ) : rows.length === 0 ? (
                    <p className="text-center text-sm text-gray-400 py-8">No transactions found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-100 dark:bg-gray-900/40">
                                <tr>
                                    {['CC Code', 'Invoice No', 'Invoice Date', 'Received Date', 'PO No', 'Client Code', 'Sub Client', 'Bank Name', 'Amount', 'Type', 'Payment Type'].map((h) => (
                                        <th key={h} className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {rows.map((r, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10">
                                        <td className="px-2 py-1 whitespace-nowrap">{r.CCCode}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.InvoiceNo}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.InvoiceDate}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.ReceivedDate}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.PONO}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.ClientCode}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.SubClientCode}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.BankName}</td>
                                        <td className="px-2 py-1 text-right font-semibold">{fmt(r.Amount)}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.Type}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{r.PaymentType}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    </div>
);

const ConsolidateCashflowReport = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    const {
        ccTypes, ccTypesLoading, years, yearsLoading, ccList, ccListLoading,
        report, reportLoading, reportError, detailByRow, detailLoadingByRow,
        invoiceDetail, invoiceDetailLoading,
    } = useSelector(selectConsolidateCashflowReport);

    const [ccType, setCcType] = useState('');
    const [ccStatus, setCcStatus] = useState('');
    const [selectedCCs, setSelectedCCs] = useState([]);
    const [year, setYear] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    const [modal, setModal] = useState(null); // { title }
    const [queryParams, setQueryParams] = useState(null); // last-run params, needed for drill-down + export

    useEffect(() => {
        dispatch(fetchCCTypes());
        dispatch(fetchFinancialYears());
    }, [dispatch]);

    useEffect(() => {
        if (ccType && ccStatus && roleId && uid) {
            dispatch(fetchCCList({ ApplicableCCTypes: ccType, Roleid: roleId, UID: uid, CCStatus: ccStatus }));
        }
        setSelectedCCs([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccType, ccStatus]);

    const toggleCC = (code) => setSelectedCCs((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]));
    const toggleAllCC = () => setSelectedCCs((p) => (p.length === ccList.length ? [] : ccList.map((c) => c.CCValue)));

    const handleReset = () => {
        setCcType(''); setCcStatus(''); setSelectedCCs([]); setYear('');
        setExpandedRows({}); setQueryParams(null);
        dispatch(clearReport());
    };

    const runReport = () => {
        if (!ccType) { toast.warning('Select a Cost Centre Type.'); return; }
        if (!ccStatus) { toast.warning('Select a Cost Centre Status.'); return; }
        if (selectedCCs.length === 0) { toast.warning('Select at least one Cost Centre.'); return; }
        if (!year) { toast.warning('Select a Year.'); return; }

        const yearObj = years.find((y) => y.StartYear === year);
        const startYr = year;
        const endYr = String(parseInt(year, 10) + 1);
        const prevYr = String(parseInt(year, 10) - 1);
        const fyYear = yearObj?.YearValue || year;
        const applicableCCS = selectedCCs.length === ccList.length ? '0' : joinIds(selectedCCs);

        const params = { CCCode: applicableCCS, StartYear: startYr, EndYear: endYr, PrevYear: prevYr, CCtypes: ccType, Roleid: roleId, UID: uid, CCStatus: ccStatus, FYYear: fyYear };
        setQueryParams(params);
        setExpandedRows({});
        dispatch(fetchConsolidateCashflowReport(params));
    };

    const handleRefresh = () => { if (queryParams) dispatch(fetchConsolidateCashflowReport(queryParams)); };

    const rows = report?.ConsolidateData || [];
    const overhead = report?.ConsolidateoverheadData?.[0];
    const showPYColumns = ccType === 'Performing' && queryParams?.CCCode !== '0';
    const showPYOverhead = showPYColumns;

    const totals = useMemo(() => rows.reduce((acc, r) => {
        ['PaidCash', 'PaidCheque', 'PaidTotal', 'PayableCash', 'PayableCheque', 'Payabletotal', 'TotalCash', 'TotalCheque', 'TotalGross', 'PYPaid', 'PYPayable', 'PYTotal', 'CUTotal'].forEach((k) => {
            acc[k] = (acc[k] || 0) + (parseFloat(r[k]) || 0);
        });
        return acc;
    }, {}), [rows]);

    const toggleRow = (row, idx) => {
        const rowKey = `${row.DCACode}-${idx}`;
        if (!row.DCACode) return;
        setExpandedRows((p) => ({ ...p, [rowKey]: !p[rowKey] }));
        if (!detailByRow[rowKey] && !expandedRows[rowKey]) {
            dispatch(fetchCashflowDetail({
                rowKey, DCACode: row.DCACode, CCCode: queryParams.CCCode, Year1: queryParams.StartYear, Year2: queryParams.EndYear, CCType: ccType, CCStatus: ccStatus,
            }));
        }
    };

    const openInvoiceModal = (type, searchType, amount) => {
        if (!amount) return;
        const titles = { Basic: 'Total Basic Invoice', Receipts: 'Total Receipts', TDS: 'TDS' };
        setModal({ title: `${titles[type]} — ${searchType} Year` });
        dispatch(fetchInvoiceReceiptDetail({
            CCCode: queryParams.CCCode, FYyear: queryParams.FYYear, Type: type, ReportType: 'Cashflow', CCtype: ccType, Searchtype: searchType,
        }));
    };

    const closeModal = () => { setModal(null); dispatch(clearInvoiceDetail()); };

    const handleExport = () => {
        if (rows.length === 0) { toast.warning('No data to export'); return; }
        const wb = XLSX.utils.book_new();
        const mainRows = [...rows, { DCACode: '', DCAName: 'TOTAL', ...totals }].map((r) => {
            const base = {
                'DCA Code': r.DCACode, 'DCA Name': r.DCAName,
                'Paid Cash': r.PaidCash, 'Paid Cheque': r.PaidCheque, 'Paid Total': r.PaidTotal,
                'Payable Cash': r.PayableCash, 'Payable Cheque': r.PayableCheque, 'Payable Total': r.Payabletotal,
                'Total Cash': r.TotalCash, 'Total Cheque': r.TotalCheque, 'Gross Total': r.TotalGross,
            };
            if (showPYColumns) {
                base['PY Paid'] = r.PYPaid; base['PY Payable'] = r.PYPayable; base['PY Total'] = r.PYTotal; base['CU Total'] = r.CUTotal;
            }
            return base;
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(mainRows), 'Cashflow Summary');
        if (overhead) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
                'Total Basic Invoice (Cur)': overhead.BasicInvValueCurrentYear, 'Total Basic Invoice (Total)': overhead.BasicInvValueTotal,
                'Total Receipts (Cur)': overhead.curyearReceived, 'Total Receipts (Total)': overhead.TotalReceived,
                'TDS (Cur)': overhead.curyearTds, 'TDS (Total)': overhead.TotalTds,
                'Overhead %': overhead.OverheadPer,
            }]), 'Overhead Summary');
        }
        XLSX.writeFile(wb, `Consolidate_CashFlow_${queryParams?.FYYear || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Wallet className="h-4 w-4 text-white" />
                            </div>
                            Consolidate Cash Flow Summary
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Paid, payable and total cash/cheque summary by account head</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Consolidate Cash Flow Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !queryParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre Type</label>
                        <select className={inputCls} value={ccType} onChange={(e) => setCcType(e.target.value)} disabled={ccTypesLoading}>
                            <option value="">-Please Select-</option>
                            {ccTypes.map((t) => <option key={t.CCTypeID} value={t.CCTypeDescription}>{t.CCTypeDescription}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre Status</label>
                        <select className={inputCls} value={ccStatus} onChange={(e) => setCcStatus(e.target.value)}>
                            <option value="">-Please Select-</option>
                            <option value="Active">Active</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Year</label>
                        <select className={inputCls} value={year} onChange={(e) => setYear(e.target.value)} disabled={yearsLoading}>
                            <option value="">-Please Select-</option>
                            {years.map((y, i) => <option key={i} value={y.StartYear}>{y.YearValue}</option>)}
                        </select>
                    </div>
                    <div className="flex items-end gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                        <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>

                    <div className="md:col-span-4">
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider">Cost Centres</label>
                            {ccList.length > 0 && (
                                <button onClick={toggleAllCC} className="text-[11px] font-semibold text-indigo-600 hover:underline">
                                    {selectedCCs.length === ccList.length ? 'Clear All' : 'Select All'}
                                </button>
                            )}
                        </div>
                        <div className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-2 max-h-28 overflow-y-auto grid grid-cols-2 md:grid-cols-4 gap-1">
                            {ccListLoading ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" />
                            ) : !ccType || !ccStatus ? (
                                <span className="text-[11px] text-gray-400 col-span-full">Select a Cost Centre Type and Status first.</span>
                            ) : ccList.length === 0 ? (
                                <span className="text-[11px] text-gray-400 col-span-full">No cost centres found.</span>
                            ) : (
                                ccList.map((c) => (
                                    <label key={c.CCValue} className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300 cursor-pointer">
                                        <input type="checkbox" checked={selectedCCs.includes(c.CCValue)} onChange={() => toggleCC(c.CCValue)} className="rounded border-gray-300 h-3 w-3" />
                                        {c.CCText}
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : queryParams && rows.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No data found.</div>
            ) : rows.length > 0 ? (
                <>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                            <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Cash Flow Summary — {queryParams.FYYear}</h2>
                        </div>
                        <div className="overflow-auto max-h-[70vh]">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-1.5 py-1" />
                                        <th className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase">DCA Code</th>
                                        <th className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase">DCA Name</th>
                                        <th colSpan={3} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Paid</th>
                                        <th colSpan={3} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Payable</th>
                                        <th colSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Total</th>
                                        <th className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Gross Total</th>
                                        {showPYColumns && <th colSpan={4} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Up To Previous Year</th>}
                                    </tr>
                                    <tr className="bg-indigo-700/60">
                                        <th className="px-1.5 py-1" /><th /><th />
                                        {['Cash', 'Cheque', 'Total', 'Cash', 'Cheque', 'Total', 'Cash', 'Cheque'].map((h, i) => (
                                            <th key={i} className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100">{h}</th>
                                        ))}
                                        <th className="px-2 py-1" />
                                        {showPYColumns && ['Paid', 'Payable', 'Total', 'CU Total'].map((h) => (
                                            <th key={h} className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {rows.map((row, i) => {
                                        const rowKey = `${row.DCACode}-${i}`;
                                        const expanded = expandedRows[rowKey];
                                        return (
                                            <React.Fragment key={rowKey}>
                                                <tr className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                                    <td className="px-1.5 py-1">
                                                        {row.DCACode && (
                                                            <button onClick={() => toggleRow(row, i)} className="text-indigo-500 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded p-0.5">
                                                                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td className={`${td} font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap`}>{row.DCACode}</td>
                                                    <td className={`${td} text-gray-800 dark:text-gray-100 whitespace-nowrap`}>{row.DCAName}</td>
                                                    <td className={`${td} text-right`}>{fmt(row.PaidCash)}</td>
                                                    <td className={`${td} text-right`}>{fmt(row.PaidCheque)}</td>
                                                    <td className={`${td} text-right font-semibold`}>{fmt(row.PaidTotal)}</td>
                                                    <td className={`${td} text-right`}>{fmt(row.PayableCash)}</td>
                                                    <td className={`${td} text-right`}>{fmt(row.PayableCheque)}</td>
                                                    <td className={`${td} text-right font-semibold`}>{fmt(row.Payabletotal)}</td>
                                                    <td className={`${td} text-right`}>{fmt(row.TotalCash)}</td>
                                                    <td className={`${td} text-right`}>{fmt(row.TotalCheque)}</td>
                                                    <td className={`${td} text-right font-bold text-indigo-700 dark:text-indigo-400`}>{fmt(row.TotalGross)}</td>
                                                    {showPYColumns && (
                                                        <>
                                                            <td className={`${td} text-right`}>{fmt(row.PYPaid)}</td>
                                                            <td className={`${td} text-right`}>{fmt(row.PYPayable)}</td>
                                                            <td className={`${td} text-right`}>{fmt(row.PYTotal)}</td>
                                                            <td className={`${td} text-right font-bold`}>{fmt(row.CUTotal)}</td>
                                                        </>
                                                    )}
                                                </tr>
                                                {expanded && (
                                                    <tr><td colSpan={showPYColumns ? 15 : 11}><CashflowDetailRow row={{ data: detailByRow[rowKey], loading: detailLoadingByRow[rowKey] }} /></td></tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                        <td className="px-1.5 py-1" /><td className="px-2 py-1" />
                                        <td className={td}>TOTAL</td>
                                        <td className={`${td} text-right`}>{fmt(totals.PaidCash)}</td>
                                        <td className={`${td} text-right`}>{fmt(totals.PaidCheque)}</td>
                                        <td className={`${td} text-right`}>{fmt(totals.PaidTotal)}</td>
                                        <td className={`${td} text-right`}>{fmt(totals.PayableCash)}</td>
                                        <td className={`${td} text-right`}>{fmt(totals.PayableCheque)}</td>
                                        <td className={`${td} text-right`}>{fmt(totals.Payabletotal)}</td>
                                        <td className={`${td} text-right`}>{fmt(totals.TotalCash)}</td>
                                        <td className={`${td} text-right`}>{fmt(totals.TotalCheque)}</td>
                                        <td className={`${td} text-right text-indigo-700 dark:text-indigo-400`}>{fmt(totals.TotalGross)}</td>
                                        {showPYColumns && (
                                            <>
                                                <td className={`${td} text-right`}>{fmt(totals.PYPaid)}</td>
                                                <td className={`${td} text-right`}>{fmt(totals.PYPayable)}</td>
                                                <td className={`${td} text-right`}>{fmt(totals.PYTotal)}</td>
                                                <td className={`${td} text-right`}>{fmt(totals.CUTotal)}</td>
                                            </>
                                        )}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Overhead panel — Performing CC type only */}
                    {ccType === 'Performing' && overhead && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Overhead Summary</h2>
                                <div className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 rounded-full">
                                    <Info className="h-3 w-3" />Overhead: {fmt(overhead.OverheadPer)}%
                                </div>
                            </div>
                            <div className="grid grid-cols-4 gap-2 px-3 py-1 bg-gray-50 dark:bg-gray-900/40 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                <div />
                                <div className="text-right">Current Year</div>
                                {showPYOverhead ? <div className="text-right">Prev Year</div> : <div />}
                                <div className="text-right">Total</div>
                            </div>
                            <OverheadRow label="Total Basic Invoice" cur={overhead.BasicInvValueCurrentYear} prev={overhead.BasicInvValuePrevyear} total={overhead.BasicInvValueTotal} showPrev={showPYOverhead}
                                onClickCur={() => openInvoiceModal('Basic', 'Current', overhead.BasicInvValueCurrentYear)}
                                onClickPrev={() => openInvoiceModal('Basic', 'Prev', overhead.BasicInvValuePrevyear)}
                                onClickTotal={() => openInvoiceModal('Basic', 'Total', overhead.BasicInvValueTotal)} />
                            <OverheadRow label="Total Receipts (Inv, Retention, Hold, Advance)" cur={overhead.curyearReceived} prev={overhead.prevyearReceived} total={overhead.TotalReceived} showPrev={showPYOverhead}
                                onClickCur={() => openInvoiceModal('Receipts', 'Current', overhead.curyearReceived)}
                                onClickPrev={() => openInvoiceModal('Receipts', 'Prev', overhead.prevyearReceived)}
                                onClickTotal={() => openInvoiceModal('Receipts', 'Total', overhead.TotalReceived)} />
                            <OverheadRow label="TDS" cur={overhead.curyearTds} prev={overhead.prevyearTds} total={overhead.TotalTds} showPrev={showPYOverhead}
                                onClickCur={() => openInvoiceModal('TDS', 'Current', overhead.curyearTds)}
                                onClickPrev={() => openInvoiceModal('TDS', 'Prev', overhead.prevyearTds)}
                                onClickTotal={() => openInvoiceModal('TDS', 'Total', overhead.TotalTds)} />
                            <OverheadRow label="StockTransfer Credit" cur={overhead.curyearStockTransferStatus} prev={overhead.prevyearStockTransferStatus} total={overhead.TotalStockTransferStatus} showPrev={showPYOverhead} />
                            <OverheadRow label="Bad Debts / Written off Receivables" cur={overhead.curyearBadDebt} prev={overhead.prevyearBadDebt} total={overhead.TotalBadDebt} showPrev={showPYOverhead} />
                            <OverheadRow label="Credit Note Issued" cur={overhead.curyearCreditNote} prev={overhead.prevyearCreditNote} total={overhead.TotalCreditNote} showPrev={showPYOverhead} />
                            <OverheadRow label="Debit Note Issued" cur={overhead.curyearDebitNote} prev={overhead.prevyearDebitNote} total={overhead.TotalDebitNote} showPrev={showPYOverhead} />
                            <OverheadRow label="Net Status With Receipt" cur={overhead.curyearNetStatusReciept} prev={overhead.prevyearNetStatusReciept} total={overhead.TotalNetStatusReciept} showPrev={showPYOverhead} />
                            <OverheadRow label="Net Status With Invoice" cur={overhead.curyearNetStatusInvoice} prev={overhead.prevyearNetStatusInvoice} total={overhead.TotalNetStatusInvoice} showPrev={showPYOverhead} />
                        </div>
                    )}
                </>
            ) : null}

            {modal && (
                <InvoiceDetailModal title={modal.title} rows={invoiceDetail} loading={invoiceDetailLoading} onClose={closeModal} />
            )}
        </div>
    );
};

export default ConsolidateCashflowReport;
